import {
    sendOtp,
    registerUser,
    verifyOtp,
    getLoyaltyProfile,
    updateProfile
} from '@/services/api';

const STORAGE_KEY = 'sk_loyalty_auth';
const SESSION_TTL = 15 * 60 * 1000;

function loadPersistedAuth() {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (raw) {
            const data = JSON.parse(raw);
            if (data.contactUuid && Date.now() < data.expiresAt) {
                return data;
            }
            sessionStorage.removeItem(STORAGE_KEY);
        }
    } catch {
        // ignore
    }
    return null;
}

function persistAuth(data) {
    try {
        sessionStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({ ...data, expiresAt: Date.now() + SESSION_TTL })
        );
    } catch {
        // ignore
    }
}

function clearPersistedAuth() {
    try {
        sessionStorage.removeItem(STORAGE_KEY);
    } catch {
        // ignore
    }
}

const persisted = loadPersistedAuth();

const state = () => ({
    contactUuid: persisted?.contactUuid || null,
    email: persisted?.email || '',
    firstName: persisted?.firstName || '',
    lastName: persisted?.lastName || '',
    contactCode: persisted?.contactCode || null,
    profileCompleted: persisted?.profileCompleted || false,
    balance: null,
    rewards: [],
    vouchers: [],
    tier: null,
    walletPassUrl: null,
    loading: false,
    error: null
});

const getters = {
    isAuthenticated: state => !!state.contactUuid,
    points: state => state.balance,
    displayName: state => {
        if (state.firstName) {
            return state.firstName;
        }
        return state.email;
    },
    contactCode: state => {
        return state.contactCode || null;
    },
    walletPassUrl: state => {
        const ua = navigator.userAgent || '';
        const isAndroid = /android/i.test(ua);
        if (isAndroid) {
            return state.walletPassUrl?.android || null;
        }
        return state.walletPassUrl?.apple || null;
    }
};

const mutations = {
    SET_AUTH(state, { contactUuid, email, firstName, lastName, contactCode }) {
        state.contactUuid = contactUuid;
        state.email = email || '';
        state.firstName = firstName || '';
        state.lastName = lastName || '';
        state.contactCode = contactCode || null;
        persistAuth({ contactUuid, email, firstName, lastName, contactCode, profileCompleted: state.profileCompleted });
    },
    SET_BALANCE(state, balance) {
        state.balance = balance;
    },
    SET_REWARDS(state, { rewards, vouchers }) {
        state.rewards = rewards || [];
        state.vouchers = vouchers || [];
    },
    SET_TIER(state, tier) {
        state.tier = tier;
    },
    SET_WALLET_PASS_URL(state, walletPassUrl) {
        state.walletPassUrl = walletPassUrl;
    },
    SET_PROFILE_COMPLETED(state, value) {
        state.profileCompleted = value;
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (raw) {
            try {
                const data = JSON.parse(raw);
                data.profileCompleted = value;
                sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
            } catch {
                // ignore
            }
        }
    },
    SET_LOADING(state, loading) {
        state.loading = loading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    LOGOUT(state) {
        state.contactUuid = null;
        state.email = '';
        state.firstName = '';
        state.lastName = '';
        state.contactCode = null;
        state.profileCompleted = false;
        state.balance = null;
        state.rewards = [];
        state.vouchers = [];
        state.tier = null;
        state.walletPassUrl = null;
        clearPersistedAuth();
    }
};

const actions = {
    async sendOtp(_, { email }) {
        const accountId = import.meta.env.VITE_ACCOUNT_ID;
        try {
            return await sendOtp({ accountId, email });
        } catch (err) {
            const message = err.response?.data?.message;
            if (message === 'customer_not_found') {
                return await registerUser({ accountId, email });
            }
            throw err;
        }
    },

    async verifyOtp({ commit }, { email, code }) {
        const accountId = import.meta.env.VITE_ACCOUNT_ID;
        const { data } = await verifyOtp({ accountId, email, code });
        const membership = data.membership || {};

        const isNew = data.isNew || !membership.firstName;

        commit('SET_AUTH', {
            contactUuid: membership.uuid,
            email: membership.email || email,
            firstName: membership.firstName || '',
            lastName: membership.lastName || '',
            contactCode: null
        });

        if (!isNew) {
            commit('SET_PROFILE_COMPLETED', true);
        }

        return { ...data, isNew };
    },

    async completeProfile({ state, commit }, { profile, optIns }) {
        const accountId = import.meta.env.VITE_ACCOUNT_ID;
        await updateProfile({
            accountId,
            contactUuid: state.contactUuid,
            profile,
            optIns
        });

        commit('SET_AUTH', {
            contactUuid: state.contactUuid,
            email: state.email,
            firstName: profile.firstName || state.firstName,
            lastName: profile.lastName || state.lastName,
            contactCode: state.contactCode
        });
        commit('SET_PROFILE_COMPLETED', true);
    },

    async loadLoyaltyData({ state, commit }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        try {
            const accountId = import.meta.env.VITE_ACCOUNT_ID;
            const { data } = await getLoyaltyProfile({
                accountId,
                contactUuid: state.contactUuid
            });

            commit('SET_BALANCE', data.balance || 0);
            commit('SET_REWARDS', {
                rewards: data.rewards || [],
                vouchers: data.vouchers || []
            });
            if (data.tier) {
                commit('SET_TIER', data.tier);
            }
            if (data.qr?.walletPassUrl) {
                commit('SET_WALLET_PASS_URL', data.qr.walletPassUrl);
            }
            if (data.contact) {
                commit('SET_AUTH', {
                    contactUuid: state.contactUuid,
                    email: data.contact.email || state.email,
                    firstName: data.contact.firstName || state.firstName,
                    lastName: data.contact.lastName || state.lastName,
                    contactCode: data.contact.contactCode || state.contactCode
                });
            }
        } catch (error) {
            commit('SET_ERROR', error.message || 'Failed to load loyalty data');
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    logout({ commit }) {
        commit('LOGOUT');
    }
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
